import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { hideConfirmModal } from '../../store/slices/confirmModal/confirmModalSlice';
import { clearConfirmCallback, executeConfirmCallback } from '../../features/modals/confirmCallbackRegistry';

const ConfirmModal = () => {
  const dispatch = useDispatch();
  const modalRef = useRef<HTMLDivElement>(null);

  const {
    isOpen,
    title,
    message,
    icon,
    confirmText = 'Confirmar',
    cancelText = 'Cancelar',
    confirmArgs,
  } = useSelector((state: RootState) => state.confirmModal);

  // ESC cancela, Enter confirma
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        clearConfirmCallback();
        dispatch(hideConfirmModal());
      } else if (event.key === 'Enter') {
        event.preventDefault();
        dispatch(hideConfirmModal());
        executeConfirmCallback(confirmArgs);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, dispatch, confirmArgs]);

  if (!isOpen) return null;

  const getIcon = (type: string | undefined) => {
    switch (type) {
      case 'success':
        return (
          <svg width="60" height="60" viewBox="0 0 60 60" fill="none">
            <circle cx="30" cy="30" r="30" fill="#22C55E" fillOpacity="0.1" />
            <path
              d="M20 30L27 37L40 23"
              stroke="#22C55E"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        );
      case 'error':
        return (
          <svg width="60" height="60" viewBox="0 0 60 60" fill="none">
            <circle cx="30" cy="30" r="30" fill="#DC2626" fillOpacity="0.1" />
            <path
              d="M22 22L38 38M38 22L22 38"
              stroke="#DC2626"
              strokeWidth="3"
              strokeLinecap="round"
            />
          </svg>
        );
      case 'info':
        return (
          <svg width="60" height="60" viewBox="0 0 60 60" fill="none">
            <circle cx="30" cy="30" r="30" fill="#3B82F6" fillOpacity="0.1" />
            <path
              d="M30 20V32M30 40H30.01"
              stroke="#3B82F6"
              strokeWidth="3"
              strokeLinecap="round"
            />
          </svg>
        );
      default:
        return (
          <svg width="60" height="60" viewBox="0 0 60 60" fill="none">
            <rect opacity="0.1" width="60" height="60" rx="30" fill="#F59E0B" />
            <path
              d="M30 27.2498V29.9998V27.2498ZM30 35.4999H30.0134H30ZM20.6914 41H39.3086C41.3778 41 42.6704 38.7078 41.6358 36.8749L32.3272 20.3747C31.2926 18.5418 28.7074 18.5418 27.6728 20.3747L18.3642 36.8749C17.3296 38.7078 18.6222 41 20.6914 41Z"
              stroke="#F59E0B"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        );
    }
  };

  const getButtonColor = () => {
    switch (icon) {
      case 'success':
        return 'bg-green-600 hover:bg-green-700 border-green-600 text-white';
      case 'error':
        return 'bg-red hover:bg-red-700 border-red-600 text-white';
      case 'info':
        return 'bg-blue-600 hover:bg-blue-700 border-blue-600 text-white';
      default:
        return 'bg-yellow-500 hover:bg-yellow-600 border-yellow-500 text-black';
    }
  };

  return (
    <div className="fixed left-0 top-0 z-999999 flex h-full min-h-screen w-full items-center justify-center bg-black/90 px-4 py-5">
      <div
        ref={modalRef}
        className="w-full max-w-142.5 rounded-lg bg-white px-8 py-12 text-center dark:bg-boxdark md:px-17.5 md:py-15"
      >
        <span className="mx-auto inline-block">{getIcon(icon)}</span>
        <h3 className="mt-5.5 pb-2 text-xl font-bold text-black dark:text-white sm:text-2xl">
          {title}
        </h3>
        <div className="text-gray-800 dark:text-gray-300 mb-10 whitespace-pre-line text-left leading-relaxed">
          {message}
        </div>
        <div className="-mx-3 flex flex-wrap gap-y-4">
          <div className="w-full px-3 2xsm:w-1/2">
            <button
              onClick={() => {
                clearConfirmCallback();
                dispatch(hideConfirmModal());
              }}
              className="block w-full rounded border border-stroke bg-gray p-3 text-center font-medium text-black transition hover:border-meta-1 hover:bg-meta-1 hover:text-white dark:border-strokedark dark:bg-meta-4 dark:text-white dark:hover:border-meta-1 dark:hover:bg-meta-1"
            >
              {cancelText}
            </button>
          </div>
          <div className="w-full px-3 2xsm:w-1/2">
            <button
              onClick={async () => {
                dispatch(hideConfirmModal());
                await executeConfirmCallback(confirmArgs);
              }}
              className={`block w-full rounded border p-3 text-center font-medium transition ${getButtonColor()}`}
            >
              {confirmText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmModal;
